// /auth/callback — lands a user handed off from the PWA (ADR-0045).
//
// Reads the one-time code from the URL, exchanges it at auth-svc for a
// fresh session, writes it to the dashboard's store, then routes into
// the shell. Registered as a PUBLIC route (outside RequireAuth) because
// its whole job is to PRODUCE the session the guards require.
//
// Ordering that matters:
//   1. Strip the code from history FIRST, synchronously, before any
//      async work — it must not survive in the address bar or the
//      back-stack (shared-phone threat model).
//   2. A code ALWAYS wins over a stored session: it is the identity the
//      user just chose in the PWA. Skipping the exchange because some
//      session existed landed people in the PREVIOUS account (another
//      user, another org) after switching accounts on the phone.
//   3. Exchange; on success setSession (replacing whatever was stored) +
//      navigate; on any failure drop the stored session too — keeping
//      it would be the same wrong-account bug — and route to /login with
//      a recoverable flag.
//   4. No code at all: an existing session goes straight in, otherwise
//      /login.

import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { handoffApi } from "../lib/api/endpoints/sso";
import { clearAuth, currentSession, setSession } from "../lib/api/sessionStore";

export function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Guards React StrictMode's double-effect in dev so we never exchange
  // a single-use code twice.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const code = params.get("code");
    const rawRedirect = params.get("redirect") ?? "/";
    // Same-origin relative paths only — reject an absolute URL to close
    // the open-redirect vector.
    const safeRedirect = rawRedirect.startsWith("/") ? rawRedirect : "/";
    // Si el paso falla, /login recibe el destino completo (ruta + query)
    // como `from`, igual que desde RequireAuth: el deep link sobrevive.
    const target = new URL(safeRedirect, "http://dashboard.local");
    const loginState = {
      ssoError: true,
      from: { pathname: target.pathname, search: target.search },
    };

    // Strip the artifact from history before anything can fail or log.
    window.history.replaceState(null, "", window.location.pathname);

    if (!code) {
      if (currentSession()) navigate(safeRedirect, { replace: true });
      else navigate("/login", { replace: true, state: loginState });
      return;
    }

    // No abort on cleanup: StrictMode's dev double-effect runs this
    // cleanup right after the first mount, and `attempted` keeps the
    // second run from retrying — aborting here left the single-use code
    // unexchanged and dumped every local handoff on /login. The request
    // is one-shot and its result is the whole point of the route, so it
    // is allowed to finish.
    handoffApi
      .exchange(code)
      .then((pair) => {
        setSession(pair);
        navigate(safeRedirect, { replace: true });
      })
      .catch(() => {
        clearAuth();
        navigate("/login", { replace: true, state: loginState });
      });
    // One-shot on mount: exchange the code exactly once. params/navigate
    // are stable enough for this imperative flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="auth-page">
      <div className="auth-card">
        <p className="auth-card__sub">Iniciando sesión…</p>
      </div>
    </main>
  );
}
